import type { RenderParams } from "./render-client";

const JUST_RATIOS = [
  1, 16 / 15, 9 / 8, 6 / 5, 5 / 4, 4 / 3, 45 / 32,
  3 / 2, 8 / 5, 5 / 3, 9 / 5, 15 / 8, 2,
] as const;

const RAGA_PHRASES: Array<[string[], number[]]> = [
  [["saurashtra"], [0, 2, 4, 5, 7, 9, 11, 12, 11, 9, 7, 5, 4, 2, 0]],
  [["chandrakauns"], [0, 3, 5, 8, 11, 12, 11, 8, 5, 3, 0]],
  [["hamsadhwani", "hamsadhvani"], [0, 2, 4, 7, 11, 12, 11, 7, 4, 2, 0]],
  [["kalyani", "mechakalyani"], [0, 2, 4, 6, 7, 9, 11, 12, 11, 9, 7, 6, 4, 2, 0]],
  [["sankarabharanam", "shankarabharanam"], [0, 2, 4, 5, 7, 9, 11, 12, 11, 9, 7, 5, 4, 2, 0]],
  [["hindolam", "malkauns"], [0, 3, 5, 8, 10, 12, 10, 8, 5, 3, 0]],
  [["charukesi"], [0, 2, 4, 5, 7, 8, 10, 12, 10, 8, 7, 5, 4, 2, 0]],
  [["bhairavi"], [0, 2, 3, 5, 7, 8, 10, 12, 10, 8, 7, 5, 3, 2, 0]],
  [["bhairav", "mayamalavagowla"], [0, 1, 4, 5, 7, 8, 11, 12, 11, 8, 7, 5, 4, 1, 0]],
  [["varali"], [0, 1, 6, 7, 11, 12, 11, 7, 6, 1, 0]],
  [["revati"], [0, 1, 5, 7, 10, 12, 10, 7, 5, 1, 0]],
  [["mohanam"], [0, 2, 4, 7, 9, 12, 9, 7, 4, 2, 0]],
];

export function ragaPhrase(name: string): number[] {
  const key = name.toLowerCase().replace(/[\s()_-]/g, "");
  return RAGA_PHRASES.find(([aliases]) => aliases.some((alias) => key.includes(alias)))?.[1]
    ?? RAGA_PHRASES[RAGA_PHRASES.length - 1][1];
}

export function justRatio(step: number): number {
  const octave = Math.floor(step / 12);
  const degree = ((step % 12) + 12) % 12;
  return JUST_RATIOS[degree] * 2 ** octave;
}

/** Immediate, dependency-free tambura + veena-like phrase + mridangam pulse. */
export class LocalRagaPlayer {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextNoteAt = 0;
  private phraseIndex = 0;
  private params: Required<Pick<RenderParams, "ragaName" | "baseHz" | "tempoBpm">> | null = null;
  private volume = 0.7;

  async start(params: RenderParams): Promise<void> {
    this.stop();
    const Context = window.AudioContext
      || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) throw new Error("This browser does not support on-device audio.");
    const context = new Context();
    const master = context.createGain();
    master.gain.value = this.volume * 0.42;
    master.connect(context.destination);
    this.context = context;
    this.master = master;
    this.params = {
      ragaName: params.ragaName || "Mohanam",
      baseHz: Math.min(440, Math.max(110, params.baseHz || 220)),
      tempoBpm: Math.min(144, Math.max(48, params.tempoBpm || 84)),
    };
    await context.resume();
    this.startTambura();
    this.nextNoteAt = context.currentTime + 0.04;
    this.schedulePhrase();
    this.timer = setInterval(() => this.schedulePhrase(), 500);
  }

  private startTambura() {
    const context = this.context;
    const master = this.master;
    const params = this.params;
    if (!context || !master || !params) return;
    [1, 1.5, 2, 2.006].forEach((ratio, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = index === 1 ? "triangle" : "sine";
      oscillator.frequency.value = params.baseHz * ratio;
      gain.gain.value = index === 0 ? 0.10 : 0.045;
      oscillator.connect(gain).connect(master);
      oscillator.start();
    });
  }

  private schedulePhrase() {
    const context = this.context;
    const master = this.master;
    const params = this.params;
    if (!context || !master || !params || context.state === "closed") return;
    const phrase = ragaPhrase(params.ragaName);
    const beat = 60 / params.tempoBpm;
    while (this.nextNoteAt < context.currentTime + 1.5) {
      const step = phrase[this.phraseIndex % phrase.length];
      const at = this.nextNoteAt;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(params.baseHz * justRatio(step), at);
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.16, at + 0.035);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + beat * 0.82);
      oscillator.connect(gain).connect(master);
      oscillator.start(at);
      oscillator.stop(at + beat * 0.9);

      if (this.phraseIndex % 4 === 0) this.scheduleMridangam(at);
      this.phraseIndex += 1;
      this.nextNoteAt += beat;
    }
  }

  private scheduleMridangam(at: number) {
    const context = this.context;
    const master = this.master;
    if (!context || !master) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(115, at);
    oscillator.frequency.exponentialRampToValueAtTime(58, at + 0.12);
    gain.gain.setValueAtTime(0.09, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
    oscillator.connect(gain).connect(master);
    oscillator.start(at);
    oscillator.stop(at + 0.18);
  }

  setVolume(value: number) {
    this.volume = Math.min(1, Math.max(0, value));
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(this.volume * 0.42, this.context.currentTime, 0.03);
    }
  }

  pause() { void this.context?.suspend(); }
  async resume() { await this.context?.resume(); }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const context = this.context;
    this.context = null;
    this.master = null;
    this.params = null;
    this.phraseIndex = 0;
    if (context && context.state !== "closed") void context.close();
  }
}
